import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  supabase,
} from "../../lib/supabaseClient";

import {
  prpWorkspaceCommand,
} from "../../lib/prpWorkspace";

import {
  usePrpSessionState,
} from "../../lib/prpUiContinuity";

// LOT0F5_PRP_PIPELINE_TERRAIN_COCKPIT_V2

type AnyRecord =
  Record<string, any>;

type Props = {
  leads: AnyRecord[];
  activities: AnyRecord[];
  onRefresh: () => Promise<void> | void;
};

type ForfyAgentKey =
  | "forfy_prp_analyst"
  | "forfy_prp_qualifier"
  | "forfy_prp_writer";

type ForfyState = {
  loading: boolean;
  saving: boolean;
  saved: boolean;
  agent_key?: ForfyAgentKey;
  payload?: AnyRecord;
  error?: string;
};

const STAGES = [
  { key: "new", label: "Nouveau" },
  { key: "contacted", label: "Contacté" },
  { key: "meeting", label: "Rendez-vous" },
  { key: "demo", label: "Démo" },
  { key: "proposal", label: "Proposition" },
  { key: "registered", label: "Inscrit" },
  { key: "approved", label: "Approuvé", adminOnly: true },
  { key: "active", label: "Actif", adminOnly: true },
  { key: "lost", label: "Perdu" },
] as const;

const ACTIVITY_TYPES = [
  { key: "note", label: "Note" },
  { key: "call", label: "Appel" },
  { key: "visit", label: "Visite" },
  { key: "meeting", label: "Rencontre" },
  { key: "follow_up", label: "Relance" },
] as const;

const FORFY_ACTIONS: Array<{
  key: ForfyAgentKey;
  label: string;
  purpose: string;
}> = [
  {
    key: "forfy_prp_analyst",
    label: "Analyser",
    purpose: "Potentiel et angle commercial",
  },
  {
    key: "forfy_prp_qualifier",
    label: "Qualifier",
    purpose: "Priorité et prochaine action",
  },
  {
    key: "forfy_prp_writer",
    label: "Préparer l’approche",
    purpose: "Brouillon de premier contact",
  },
];

const text = (
  value: unknown,
  fallback = "—",
): string =>
  typeof value === "string"
  && value.trim()
    ? value.trim()
    : fallback;

const leadStage = (
  lead: AnyRecord,
): string =>
  text(
    lead.pipeline_stage
    || lead.stage
    || lead.status,
    "new",
  ).toLowerCase();

const leadDate = (
  value: unknown,
): string => {
  if (
    typeof value !== "string"
    || !value
  ) {
    return "—";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "—";
  }

  return date.toLocaleString(
    "fr-CA",
    {
      dateStyle: "medium",
      timeStyle: "short",
    },
  );
};

const toDateTimeLocal = (
  date: Date,
): string => {
  const local =
    new Date(
      date.getTime()
      - date.getTimezoneOffset()
        * 60000,
    );

  return local
    .toISOString()
    .slice(0, 16);
};

const invokeForfyAgent = async (
  agentKey: ForfyAgentKey,
  lead: AnyRecord,
): Promise<AnyRecord> => {
  const {
    data: { session },
    error: sessionError,
  } =
    await supabase.auth
      .getSession();

  if (sessionError) {
    throw sessionError;
  }

  const token =
    session?.access_token
      ?.trim();

  if (!token) {
    throw new Error(
      "prp_session_required",
    );
  }

  const result =
    await supabase.functions
      .invoke(
        "prp-forfy-agent-run",
        {
          body: {
            action:
              "run_prp_forfy_agent",
            agent_key:
              agentKey,
            context: {
              business_name:
                text(
                  lead.business_name,
                  "Prospect",
                ),
              email:
                text(
                  lead.email,
                  "",
                ),
              city:
                text(
                  lead.city,
                  "",
                ),
              category:
                text(
                  lead.category,
                  "",
                ),
              status:
                leadStage(
                  lead,
                ),
              notes:
                text(
                  lead.notes,
                  "",
                ),
              source:
                "prp_pipeline_terrain",
            },
          },
          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        },
      );

  if (result.error) {
    const errorData =
      result.data as AnyRecord | null;

    const code =
      typeof errorData?.error === "string"
        ? String(
            errorData.error,
          )
        : result.error.message;

    throw new Error(
      code
      || "forfy_agent_failed",
    );
  }

  const payload =
    (
      result.data
      || {}
    ) as AnyRecord;

  if (
    payload.ok
    !== true
  ) {
    throw new Error(
      text(
        payload.error,
        "forfy_agent_failed",
      ),
    );
  }

  return payload;
};

export default function PrpPipelineTerrainCockpit(
  {
    leads,
    activities,
    onRefresh,
  }: Props,
) {
  const [search, setSearch] =
    useState("");

  const [stageFilter, setStageFilter] =
    usePrpSessionState<string>(
      "pipeline.stageFilter",
      "all",
      (value) =>
        typeof value === "string"
        && (
          value === "all"
          || STAGES.some(
            (stage) =>
              stage.key === value,
          )
        ),
    );

  const [selectedLeadId, setSelectedLeadId] =
    usePrpSessionState<string | null>(
      "pipeline.selectedLeadId",
      null,
      (value) =>
        value === null
        || typeof value === "string",
    );

  const [busyKey, setBusyKey] =
    useState("");

  const [activityType, setActivityType] =
    useState("note");

  const [activitySummary, setActivitySummary] =
    useState("");

  const [nextAction, setNextAction] =
    useState("");

  const [nextActionAt, setNextActionAt] =
    useState(
      toDateTimeLocal(
        new Date(
          Date.now()
          + 24 * 60 * 60 * 1000,
        ),
      ),
    );

  const [forfy, setForfy] =
    useState<ForfyState>({
      loading: false,
      saving: false,
      saved: false,
    });

  const stageCounts =
    useMemo(
      () => {
        const counts:
          Record<string, number> =
          {};

        for (const stage of STAGES) {
          counts[stage.key] = 0;
        }

        for (const lead of leads) {
          const stage =
            leadStage(lead);

          counts[stage] =
            (counts[stage] || 0) + 1;
        }

        return counts;
      },
      [leads],
    );

  const filteredLeads =
    useMemo(
      () => {
        const query =
          search
            .trim()
            .toLowerCase();

        return leads
          .filter(
            (lead) => {
              const stage =
                leadStage(lead);

              if (
                stageFilter !== "all"
                && stage !== stageFilter
              ) {
                return false;
              }

              if (!query) {
                return true;
              }

              return [
                lead.business_name,
                lead.contact_name,
                lead.email,
                lead.phone,
                lead.next_action,
              ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(query);
            },
          )
          .sort(
            (left, right) =>
              new Date(
                right.next_action_at
                || right.updated_at
                || right.created_at
                || 0,
              ).getTime()
              - new Date(
                left.next_action_at
                || left.updated_at
                || left.created_at
                || 0,
              ).getTime(),
          );
      },
      [
        leads,
        search,
        stageFilter,
      ],
    );

  const selectedLead =
    useMemo(
      () =>
        leads.find(
          (lead) =>
            String(lead.id || "")
            === selectedLeadId,
        )
        || null,
      [
        leads,
        selectedLeadId,
      ],
    );

  useEffect(
    () => {
      if (
        selectedLeadId
        && !selectedLead
      ) {
        setSelectedLeadId(
          null,
        );
      }
    },
    [
      selectedLead,
      selectedLeadId,
      setSelectedLeadId,
    ],
  );

  const selectedActivities =
    useMemo(
      () => {
        if (!selectedLeadId) {
          return [];
        }

        return activities
          .filter(
            (activity) =>
              String(
                activity.lead_id
                || "",
              )
              === selectedLeadId,
          )
          .sort(
            (left, right) =>
              new Date(
                right.created_at
                || right.occurred_at
                || 0,
              ).getTime()
              - new Date(
                left.created_at
                || left.occurred_at
                || 0,
              ).getTime(),
          )
          .slice(0, 12);
      },
      [
        activities,
        selectedLeadId,
      ],
    );

  const refresh = async () => {
    await Promise.resolve(
      onRefresh(),
    );
  };

  const updateStage =
    async (
      leadId: string,
      stage: string,
    ) => {
      if (busyKey) return;

      const key =
        `stage:${leadId}:${stage}`;

      setBusyKey(key);

      try {
        await prpWorkspaceCommand(
          "lead_update_status",
          {
            lead_id: leadId,
            status: stage,
          },
        );

        await refresh();
      } finally {
        setBusyKey("");
      }
    };

  const addActivity =
    async () => {
      if (
        !selectedLead
        || !activitySummary.trim()
      ) {
        return;
      }

      const leadId =
        String(
          selectedLead.id
          || "",
        );

      if (!leadId) return;

      setBusyKey(
        `activity:${leadId}`,
      );

      try {
        await prpWorkspaceCommand(
          "activity_add",
          {
            lead_id: leadId,
            activity_type:
              activityType,
            summary:
              activitySummary.trim(),
            next_action:
              nextAction.trim()
              || null,
            next_action_at:
              nextAction.trim()
              && nextActionAt
                ? new Date(
                    nextActionAt,
                  ).toISOString()
                : null,
          },
        );

        setActivitySummary("");
        await refresh();
      } finally {
        setBusyKey("");
      }
    };

  const runForfy =
    async (
      agentKey: ForfyAgentKey,
    ) => {
      if (!selectedLead) {
        return;
      }

      setForfy({
        loading: true,
        saving: false,
        saved: false,
        agent_key: agentKey,
      });

      try {
        const payload =
          await invokeForfyAgent(
            agentKey,
            selectedLead,
          );

        setForfy({
          loading: false,
          saving: false,
          saved: false,
          agent_key: agentKey,
          payload,
        });
      } catch (error) {
        setForfy({
          loading: false,
          saving: false,
          saved: false,
          agent_key: agentKey,
          error:
            error instanceof Error
              ? error.message
              : "forfy_agent_failed",
        });
      }
    };

  const saveForfy =
    async () => {
      if (
        !selectedLead
        || !forfy.payload
        || !forfy.agent_key
        || forfy.saving
        || forfy.saved
      ) {
        return;
      }

      const leadId =
        String(
          selectedLead.id
          || "",
        );

      const forfyResultId =
        text(
          forfy.payload
            .forfy_result_id,
          "",
        );

      if (
        !leadId
        || !forfyResultId
      ) {
        return;
      }

      setForfy(
        (current) => ({
          ...current,
          saving: true,
        }),
      );

      try {
        await prpWorkspaceCommand(
          "forfy_result_save",
          {
            forfy_result_id:
              forfyResultId,
            agent_key:
              forfy.agent_key,
            business_name:
              text(
                selectedLead.business_name,
                "Prospect",
              ),
            lead_id:
              leadId,
            output:
              forfy.payload.output
              || {},
            runtime_meta:
              forfy.payload.runtime_meta
              || {},
            canonical_product_context_version:
              forfy.payload
                .canonical_product_context_version
              || "forfeo-products-v1",
          },
        );

        setForfy(
          (current) => ({
            ...current,
            saving: false,
            saved: true,
          }),
        );

        await refresh();
      } catch (error) {
        setForfy(
          (current) => ({
            ...current,
            saving: false,
            error:
              error instanceof Error
                ? error.message
                : "forfy_memory_save_failed",
          }),
        );
      }
    };

  return (
    <section
      className="rounded-3xl border border-violet-100 bg-gradient-to-br from-white via-white to-violet-50 p-5 shadow-sm"
      data-lot0f5-prp-pipeline-terrain="true"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.25em] text-violet-700">
            0F-5 · Cockpit terrain PRP
          </p>

          <h3 className="mt-1 text-xl font-black text-slate-950">
            Pipeline 360 + Forfy
          </h3>

          <p className="mt-1 max-w-3xl text-sm font-semibold leading-6 text-slate-600">
            Filtre les prospects, avance-les dans le pipeline, consigne les appels
            et visites, programme la prochaine action et utilise Forfy directement
            sur la fiche du prospect.
          </p>
        </div>

        <button
          className="rounded-2xl border border-violet-200 bg-white px-4 py-2 text-xs font-black text-violet-800"
          onClick={refresh}
          type="button"
        >
          Actualiser
        </button>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        {STAGES
          .filter(
            (stage) =>
              [
                "new",
                "contacted",
                "meeting",
                "proposal",
                "active",
                "lost",
              ]
                .includes(stage.key),
          )
          .map(
            (stage) => (
              <button
                className={`rounded-2xl border p-3 text-left ${
                  stageFilter === stage.key
                    ? "border-violet-400 bg-violet-100"
                    : "border-slate-200 bg-white"
                }`}
                key={stage.key}
                onClick={() =>
                  setStageFilter(
                    stageFilter === stage.key
                      ? "all"
                      : stage.key,
                  )
                }
                type="button"
              >
                <p className="text-2xl font-black text-slate-950">
                  {stageCounts[stage.key] || 0}
                </p>
                <p className="text-xs font-black uppercase tracking-wide text-slate-500">
                  {stage.label}
                </p>
              </button>
            ),
          )}
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-[1fr_220px]">
        <input
          className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-900 outline-none focus:border-violet-400"
          onChange={(event) =>
            setSearch(event.target.value)
          }
          placeholder="Rechercher entreprise, contact, courriel, prochaine action…"
          value={search}
        />

        <select
          className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700"
          onChange={(event) =>
            setStageFilter(
              event.target.value,
            )
          }
          value={stageFilter}
        >
          <option value="all">
            Toutes les étapes
          </option>

          {STAGES.map(
            (stage) => (
              <option
                key={stage.key}
                value={stage.key}
              >
                {stage.label}
              </option>
            ),
          )}
        </select>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="grid content-start gap-3 md:grid-cols-2">
          {filteredLeads.map(
            (lead) => {
              const leadId =
                String(
                  lead.id
                  || "",
                );

              const stage =
                leadStage(lead);

              const selected =
                leadId
                === selectedLeadId;

              return (
                <button
                  className={`rounded-2xl border p-4 text-left transition ${
                    selected
                      ? "border-violet-400 bg-violet-50 shadow-sm"
                      : "border-slate-200 bg-white hover:border-violet-200"
                  }`}
                  key={leadId}
                  onClick={() => {
                    setSelectedLeadId(
                      leadId,
                    );

                    setForfy({
                      loading: false,
                      saving: false,
                      saved: false,
                    });
                  }}
                  type="button"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-black text-slate-950">
                        {text(
                          lead.business_name,
                          "Prospect",
                        )}
                      </p>

                      <p className="mt-1 text-xs font-bold text-slate-500">
                        {text(
                          lead.contact_name,
                          "Contact à identifier",
                        )}
                      </p>
                    </div>

                    <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-black uppercase text-slate-600">
                      {STAGES.find(
                        (item) =>
                          item.key === stage,
                      )?.label || stage}
                    </span>
                  </div>

                  <p className="mt-3 text-sm font-semibold text-slate-600">
                    {text(
                      lead.next_action,
                      "Aucune prochaine action définie",
                    )}
                  </p>

                  <p className="mt-2 text-xs font-bold text-slate-400">
                    Échéance :{" "}
                    {leadDate(
                      lead.next_action_at,
                    )}
                  </p>
                </button>
              );
            },
          )}

          {filteredLeads.length === 0 && (
            <div className="md:col-span-2 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm font-bold text-slate-400">
              Aucun prospect pour ce filtre.
            </div>
          )}
        </div>

        <aside className="rounded-2xl border border-slate-200 bg-white p-4">
          {!selectedLead ? (
            <div className="flex min-h-80 items-center justify-center text-center">
              <div>
                <p className="text-lg font-black text-slate-900">
                  Fiche prospect 360
                </p>
                <p className="mt-2 text-sm font-semibold text-slate-500">
                  Sélectionne un prospect pour voir son historique, sa prochaine
                  action et les agents Forfy.
                </p>
              </div>
            </div>
          ) : (
            <div>
              <div className="border-b border-slate-100 pb-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-violet-700">
                  Prospect sélectionné
                </p>

                <h4 className="mt-1 text-xl font-black text-slate-950">
                  {text(
                    selectedLead.business_name,
                    "Prospect",
                  )}
                </h4>

                <div className="mt-2 space-y-1 text-sm font-semibold text-slate-600">
                  <p>
                    {text(
                      selectedLead.contact_name,
                      "Contact à identifier",
                    )}
                  </p>

                  {selectedLead.email && (
                    <p>{selectedLead.email}</p>
                  )}

                  {selectedLead.phone && (
                    <p>{selectedLead.phone}</p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Étape du pipeline
                </p>

                <div className="mt-2 flex flex-wrap gap-2">
                  {STAGES.map(
                    (stage) => {
                      const active =
                        leadStage(
                          selectedLead,
                        )
                        === stage.key;

                      const adminOnly =
                        "adminOnly" in stage
                        && stage.adminOnly === true;

                      const leadId =
                        String(
                          selectedLead.id
                          || "",
                        );

                      return (
                        <button
                          className={`rounded-full border px-3 py-2 text-xs font-black ${
                            active
                              ? "border-violet-600 bg-violet-600 text-white"
                              : "border-slate-200 bg-white text-slate-600"
                          }`}
                          disabled={
                            adminOnly
                            || busyKey
                              === `stage:${leadId}:${stage.key}`
                          }
                          key={stage.key}
                          onClick={() =>
                            updateStage(
                              leadId,
                              stage.key,
                            )
                          }
                          type="button"
                        >
                          {adminOnly
                            ? `${stage.label} · Admin`
                            : stage.label}
                        </button>
                      );
                    },
                  )}
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-black text-slate-900">
                  Consigner une activité
                </p>

                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <select
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold"
                    onChange={(event) =>
                      setActivityType(
                        event.target.value,
                      )
                    }
                    value={activityType}
                  >
                    {ACTIVITY_TYPES.map(
                      (item) => (
                        <option
                          key={item.key}
                          value={item.key}
                        >
                          {item.label}
                        </option>
                      ),
                    )}
                  </select>

                  <input
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold"
                    onChange={(event) =>
                      setNextActionAt(
                        event.target.value,
                      )
                    }
                    type="datetime-local"
                    value={nextActionAt}
                  />
                </div>

                <textarea
                  className="mt-2 min-h-24 w-full rounded-xl border border-slate-200 bg-white p-3 text-sm font-semibold"
                  onChange={(event) =>
                    setActivitySummary(
                      event.target.value,
                    )
                  }
                  placeholder="Résumé : appel effectué, visite, information obtenue…"
                  value={activitySummary}
                />

                <input
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold"
                  onChange={(event) =>
                    setNextAction(
                      event.target.value,
                    )
                  }
                  placeholder="Prochaine action : rappeler, envoyer une présentation…"
                  value={nextAction}
                />

                <button
                  className="mt-3 w-full rounded-xl bg-slate-950 px-4 py-3 text-sm font-black text-white disabled:opacity-40"
                  disabled={
                    !activitySummary.trim()
                    || busyKey.startsWith(
                      "activity:",
                    )
                  }
                  onClick={addActivity}
                  type="button"
                >
                  Enregistrer l’activité
                </button>
              </div>

              <div className="mt-5">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Forfy sur ce prospect
                </p>

                <div className="mt-2 grid gap-2">
                  {FORFY_ACTIONS.map(
                    (agent) => (
                      <button
                        className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-3 text-left"
                        disabled={forfy.loading}
                        key={agent.key}
                        onClick={() =>
                          runForfy(
                            agent.key,
                          )
                        }
                        type="button"
                      >
                        <p className="text-sm font-black text-violet-950">
                          {agent.label}
                        </p>
                        <p className="text-xs font-semibold text-violet-700">
                          {agent.purpose}
                        </p>
                      </button>
                    ),
                  )}
                </div>

                {forfy.loading && (
                  <div className="mt-3 rounded-xl bg-slate-950 p-3 text-sm font-bold text-white">
                    Forfy analyse le prospect…
                  </div>
                )}

                {forfy.error && (
                  <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs font-bold text-rose-800">
                    {forfy.error}
                  </div>
                )}

                {forfy.payload && (
                  <div className="mt-3 rounded-xl border border-violet-100 bg-white p-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-violet-700">
                        Résultat Forfy
                      </p>

                      <span className="text-[10px] font-bold text-slate-400">
                        {text(
                          forfy.payload
                            ?.runtime_meta
                            ?.provider_used,
                          "serveur",
                        )}
                      </span>
                    </div>

                    <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap rounded-xl bg-slate-950 p-3 text-[11px] font-semibold text-slate-100">
                      {JSON.stringify(
                        forfy.payload.output
                        || {},
                        null,
                        2,
                      )}
                    </pre>

                    <button
                      className="mt-3 w-full rounded-xl bg-violet-700 px-4 py-3 text-sm font-black text-white disabled:opacity-50"
                      disabled={
                        forfy.saving
                        || forfy.saved
                      }
                      onClick={saveForfy}
                      type="button"
                    >
                      {forfy.saving
                        ? "Conservation…"
                        : forfy.saved
                          ? "✓ Conservé sur le prospect"
                          : "Conserver dans l’historique du prospect"}
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-5">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                    Historique CRM
                  </p>

                  <span className="text-xs font-bold text-slate-400">
                    {selectedActivities.length}
                  </span>
                </div>

                <div className="mt-2 space-y-2">
                  {selectedActivities.map(
                    (activity) => (
                      <div
                        className="rounded-xl border border-slate-100 bg-slate-50 p-3"
                        key={
                          String(
                            activity.id
                            || `${activity.created_at}-${activity.summary}`,
                          )
                        }
                      >
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-xs font-black uppercase text-violet-700">
                            {text(
                              activity.activity_type,
                              "activité",
                            )}
                          </p>

                          <p className="text-[10px] font-bold text-slate-400">
                            {leadDate(
                              activity.created_at
                              || activity.occurred_at,
                            )}
                          </p>
                        </div>

                        <p className="mt-1 text-sm font-semibold leading-5 text-slate-700">
                          {text(
                            activity.summary,
                            "",
                          )}
                        </p>

                        {activity.next_action && (
                          <p className="mt-2 text-xs font-bold text-emerald-700">
                            → {activity.next_action}
                          </p>
                        )}
                      </div>
                    ),
                  )}

                  {selectedActivities.length === 0 && (
                    <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs font-bold text-slate-400">
                      Aucun historique pour ce prospect.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>

      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs font-bold leading-5 text-amber-900">
        Garde-fou : changer une étape, consigner une activité ou conserver une
        analyse Forfy exige une action humaine. Aucun courriel, appel, publication,
        contrat ou transaction n’est exécuté automatiquement.
      </div>
    </section>
  );
}
